import React, { useState } from 'react';
import axios from 'axios';
import { X, Send, Loader2, DollarSign } from 'lucide-react';
import { companyData } from '../data/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  companyName: '',
  contactName: '',
  email: '',
  phone: '',
  website: '',
  investmentAsk: '',
  businessSummary: '',
  sector: '',
  aum: '',
  yearsInOperation: ''
};

const InvestmentProposalModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [askError, setAskError] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'investmentAsk') {
      const numValue = parseFloat(value.replace(/[^0-9.]/g, ''));
      if (numValue && numValue < 50) {
        setAskError('Minimum investment consideration is $50M USD');
      } else {
        setAskError('');
      }
    }
  };

  const resetAndClose = () => {
    setSubmitted(false);
    setErrorMsg('');
    setAskError('');
    setFormData(initialState);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    const investmentValue = parseFloat(formData.investmentAsk.replace(/[^0-9.]/g, ''));
    if (!investmentValue || investmentValue < 50) {
      setAskError('Minimum investment consideration is $50M USD');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact/proposal`, formData);
      setSubmitted(true);
      setTimeout(resetAndClose, 4000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrorMsg(
        typeof detail === 'string'
          ? detail
          : 'Something went wrong. Please try again or email us directly.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="proposal-modal">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={resetAndClose}
        data-testid="proposal-modal-backdrop"
      />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-300">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#2f9e8e] rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            <h2 className="font-serif text-2xl font-bold text-[#1a365d]">
              Submit Investment Proposal
            </h2>
          </div>
          <button
            onClick={resetAndClose}
            className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
            data-testid="proposal-modal-close-btn"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          {submitted ? (
            <div className="text-center py-12" data-testid="proposal-success-message">
              <div className="w-16 h-16 bg-[#e8f5f3] rounded-full flex items-center justify-center mx-auto mb-4">
                <Send className="w-8 h-8 text-[#2f9e8e]" />
              </div>
              <h3 className="text-xl font-semibold text-[#1a365d] mb-2">Proposal Received</h3>
              <p className="text-gray-600">
                Thank you. Our investment committee will review your proposal and respond to the email provided.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="proposal-form">
              <div className="bg-[#e8f5f3] border border-[#d0ebe7] rounded-lg p-4 mb-6">
                <p className="text-[#1a365d] text-sm">
                  <strong>Note:</strong> This form is for investment proposals with a minimum ask of <strong>$50M USD</strong>.
                  Your submission will be reviewed by our investment team. Confirmation will be sent to your email; the proposal is routed to <span className="font-medium text-[#2f9e8e]">{companyData.contactEmail}</span>.
                </p>
              </div>

              {errorMsg && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3" data-testid="proposal-error-message">
                  {errorMsg}
                </div>
              )}

              <div className="space-y-4">
                <h3 className="font-semibold text-[#1a365d] border-b border-gray-200 pb-2">Company Information</h3>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Company Name *</label>
                    <input
                      type="text"
                      name="companyName"
                      required
                      value={formData.companyName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e] focus:border-transparent transition-all"
                      placeholder="Your Company Name"
                      data-testid="proposal-input-company-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Sector/Industry *</label>
                    <select
                      name="sector"
                      required
                      value={formData.sector}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e] focus:border-transparent transition-all"
                      data-testid="proposal-input-sector"
                    >
                      <option value="">Select Sector</option>
                      <option value="Private Credit">Private Credit</option>
                      <option value="Infrastructure">Infrastructure</option>
                      <option value="Real Estate">Real Estate</option>
                      <option value="Private Equity">Private Equity</option>
                      <option value="Venture Capital">Venture Capital</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Website</label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e] focus:border-transparent transition-all"
                      placeholder="https://www.company.com"
                      data-testid="proposal-input-website"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Years in Operation *</label>
                    <input
                      type="text"
                      name="yearsInOperation"
                      required
                      value={formData.yearsInOperation}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e] focus:border-transparent transition-all"
                      placeholder="e.g., 10 years"
                      data-testid="proposal-input-years"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Assets Under Management (AUM) *</label>
                  <input
                    type="text"
                    name="aum"
                    required
                    value={formData.aum}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e] focus:border-transparent transition-all"
                    placeholder="e.g., $500M"
                    data-testid="proposal-input-aum"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-[#1a365d] border-b border-gray-200 pb-2">Contact Information</h3>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Contact Name *</label>
                    <input
                      type="text"
                      name="contactName"
                      required
                      value={formData.contactName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e] focus:border-transparent transition-all"
                      placeholder="John Smith"
                      data-testid="proposal-input-contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e] focus:border-transparent transition-all"
                      placeholder="john@company.com"
                      data-testid="proposal-input-email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e] focus:border-transparent transition-all"
                    placeholder="+1 (555) 123-4567"
                    data-testid="proposal-input-phone"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-[#1a365d] border-b border-gray-200 pb-2">Investment Details</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Investment Ask (in $M USD) *</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="text"
                      name="investmentAsk"
                      required
                      value={formData.investmentAsk}
                      onChange={handleChange}
                      className={`w-full pl-8 pr-16 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e] focus:border-transparent transition-all ${askError ? 'border-red-400' : 'border-gray-200'}`}
                      placeholder="50"
                      data-testid="proposal-input-ask"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">M USD</span>
                  </div>
                  {askError && <p className="text-red-500 text-sm mt-1" data-testid="proposal-ask-error">{askError}</p>}
                  <p className="text-gray-500 text-xs mt-1">Minimum investment consideration: $50M USD</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Business Summary *</label>
                  <textarea
                    name="businessSummary"
                    required
                    value={formData.businessSummary}
                    onChange={handleChange}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e] focus:border-transparent transition-all resize-none"
                    placeholder="Provide a summary of your business, investment strategy, track record, and what you're looking for in a capital partner..."
                    data-testid="proposal-input-summary"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !!askError}
                className="w-full bg-[#2f9e8e] hover:bg-[#268578] text-white py-3.5 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                data-testid="proposal-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Proposal
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvestmentProposalModal;
